import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, Star, Search, ArrowLeft, Award, Shield, ChevronLeft, ChevronRight, Info, Menu, X } from 'lucide-react';
import { api } from '@/utils/api';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';
import ScrollingLogo from '@/components/ScrollingLogo';

export default function PropertiesPage() {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const ITEMS_PER_PAGE = 12;

  // Initialize filters from URL params
  const [filters, setFilters] = useState(() => {
    const params = {};
    if (searchParams.get('country')) params.country = searchParams.get('country');
    if (searchParams.get('city')) params.city = searchParams.get('city');
    if (searchParams.get('property_type')) params.property_type = searchParams.get('property_type');
    if (searchParams.get('min_capacity')) params.min_capacity = searchParams.get('min_capacity');
    if (searchParams.get('max_price')) params.max_price = searchParams.get('max_price');
    return params;
  });

  // Scroll effect for transparent header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [filters]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      // Only send filters that the backend supports (city, property_type, min_capacity, max_price)
      const backendFilters = {
        city: filters.city,
        property_type: filters.property_type,
        min_capacity: filters.min_capacity,
        max_price: filters.max_price
      };
      // Remove empty/undefined values
      Object.keys(backendFilters).forEach(key => {
        if (!backendFilters[key]) delete backendFilters[key];
      });
      const response = await api.getProperties(backendFilters);
      setProperties(response.data);
      setCurrentPage(1); // Reset to page 1 when filters change
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params to persist filters
    const newParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) newParams.set(k, v);
    });
    setSearchParams(newParams);
  };

  // Pagination logic
  const totalPages = Math.ceil(properties.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentProperties = properties.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
            : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
              <span className="font-heading text-2xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Centered Menus */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to="/services" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Services</Link>
              <Link to="/properties" className="text-gold font-medium text-sm uppercase tracking-wide">Properties</Link>
              <Link to="/gifts" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Gifting</Link>
              <Link to="/event-permissions" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Event Permits</Link>
              <Link to="/trainers" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Trainers</Link>
              <Link to="/pricing" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-navy text-navy hidden md:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/?login=true" className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
                  </Link>
                  <Link to="/bulk-rfp/new">
                    <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold shadow-lg px-4 sm:px-6 whitespace-nowrap text-sm sm:text-base">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2">
              <Link to="/services" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/properties" className="block py-3 px-4 text-gold bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
              <Link to="/gifts" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Gifting</Link>
              <Link to="/event-permissions" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Event Permits</Link>
              <Link to="/trainers" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Trainers</Link>
              <Link to="/pricing" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {user ? (
                  <>
                    <Link to={`/dashboard/${user.role}`} className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center mb-2" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block w-full py-3 px-4 border border-navy text-navy rounded-lg font-medium text-center">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/?login=true" className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative text-white py-20 mt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1561501900-3701fa6a0864?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block bg-gold/10 text-gold border border-gold/30 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
              PREMIUM VENUES
            </span>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" data-testid="properties-page-title">
              Discover Premium Venues
            </h1>
            <p className="text-white/90 text-lg max-w-3xl mx-auto">
              Explore India's finest 5-star properties for your corporate events
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Filters */}
          <div className="bg-white p-6 rounded-lg shadow-card border border-gray-100 mb-8">
            {/* Helper Text */}
            <div className="flex items-center gap-2 mb-4 p-3 bg-sky-50 rounded-lg border border-sky-200">
              <Info className="w-5 h-5 text-sky-600 flex-shrink-0" />
              <p className="text-sm text-sky-800">
                <strong>Tip:</strong> Enter minimum capacity and maximum price to find properties that match your requirements.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative">
                <label className="block text-sm font-semibold text-navy mb-2">Country</label>
                <Select 
                  value={filters.country || ''} 
                  onValueChange={(value) => handleFilterChange('country', value)}
                >
                  <SelectTrigger className="h-12 bg-white border-gray-200 text-navy">
                    <SelectValue placeholder="Select Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="india">India</SelectItem>
                    <SelectItem value="uae">UAE</SelectItem>
                    <SelectItem value="singapore">Singapore</SelectItem>
                    <SelectItem value="thailand">Thailand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="relative">
                <label className="block text-sm font-semibold text-navy mb-2">City</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-800" />
                  <Input
                    placeholder="Search by city..."
                    value={filters.city || ''}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    className="pl-10 h-12 bg-white border-gray-200 text-navy placeholder:text-slate-800"
                    data-testid="filter-city-input"
                  />
                </div>
              </div>
              
              <div className="relative">
                <label className="block text-sm font-semibold text-navy mb-2">Property Type</label>
                <Select 
                  value={filters.property_type || ''} 
                  onValueChange={(value) => handleFilterChange('property_type', value)}
                >
                  <SelectTrigger className="h-12 bg-white border-gray-200 text-navy" data-testid="filter-type-select">
                    <SelectValue placeholder="Property Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="resort">Resort</SelectItem>
                    <SelectItem value="conference">Conference Center</SelectItem>
                    <SelectItem value="banquet">Banquet Hall</SelectItem>
                  </SelectContent>
                </Select>
            </div>
            
            <div className="relative">
              <label className="block text-sm font-semibold text-navy mb-2">Min Capacity</label>
              <Input
                type="number"
                placeholder="e.g. 100"
                value={filters.min_capacity || ''}
                onChange={(e) => handleFilterChange('min_capacity', e.target.value)}
                className="h-12 bg-white border-gray-200 text-navy placeholder:text-slate-800"
                data-testid="filter-capacity-input"
              />
            </div>
            
            <div className="relative">
              <label className="block text-sm font-semibold text-navy mb-2">Max Price (₹)</label>
              <Input
                type="number"
                placeholder="e.g. 500000"
                value={filters.max_price || ''}
                onChange={(e) => handleFilterChange('max_price', e.target.value)}
                className="h-12 bg-white border-gray-200 text-navy placeholder:text-slate-800"
                data-testid="filter-price-input"
              />
            </div>
          </div>
          </div>

          {/* Results Count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-slate-700 font-medium">
              Showing {startIndex + 1}-{Math.min(endIndex, properties.length)} of {properties.length} properties
            </p>
            <p className="text-slate-800 text-sm">Page {currentPage} of {totalPages}</p>
          </div>

          {/* Properties Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-navy border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-700">Loading properties...</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-lg border">
              <p className="text-slate-700">No properties found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {currentProperties.map((property) => (
                  <Link
                    key={property.id}
                    to={`/properties/${property.id}`}
                    className="group card-premium relative"
                    data-testid={`property-card-${property.id}`}
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={property.images?.[0] || 'https://images.pexels.com/photos/1134176/pexels-photo-1134176.jpeg'}
                        alt={property.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      
                      {/* Badges */}
                      <div className="absolute top-4 left-4 flex flex-col gap-2">
                        {property.verified && (
                          <Badge className="bg-emerald-500 text-white border-0 shadow-lg">
                            <Shield className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                        {property.featured && (
                          <Badge className="bg-gold text-navy border-0 shadow-lg">
                            <Award className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                        {property.premium && (
                          <Badge className="bg-purple-600 text-white border-0 shadow-lg">
                            <Star className="w-3 h-3 mr-1" />
                            Premium
                          </Badge>
                        )}
                      </div>
                      
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-gold fill-gold" />
                          <span className="text-navy font-semibold text-sm">{property.rating || 4.5}</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      {property.brand && (
                        <span className="inline-block bg-navy-100 text-navy-800 text-xs font-bold px-2 py-1 rounded mb-2">
                          {property.brand}
                        </span>
                      )}
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2 group-hover:text-gold transition-colors">
                      {property.name}
                    </h3>
                    <div className="flex items-center gap-4 text-slate-700 text-sm mb-4">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{property.city || property.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{property.capacity} guests</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-800 mb-4 line-clamp-2">
                      {property.description}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        <span className="font-heading text-2xl font-bold text-slate-900">
                          ₹{(property.price_per_day / 1000).toFixed(0)}K
                        </span>
                        <span className="text-sm text-slate-800 ml-1">per day</span>
                      </div>
                      <Button size="sm" className="bg-navy-600 text-white hover:bg-navy-700">
                        View Details
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-12 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-navy text-navy disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNumber = idx + 1;
                    // Show first, last, current, and adjacent pages
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={pageNumber}
                          variant={currentPage === pageNumber ? "default" : "outline"}
                          onClick={() => goToPage(pageNumber)}
                          className={currentPage === pageNumber ? "bg-navy text-white" : "border-navy text-navy"}
                        >
                          {pageNumber}
                        </Button>
                      );
                    } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                      return <span key={pageNumber} className="px-2">...</span>;
                    }
                    return null;
                  })}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-navy text-navy disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
            </>
          )}
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />
    </div>
  );
}
